import { Link } from "@tanstack/react-router";
import { FaqSection } from "@/components/FaqSection";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import {
  computeCityPrices,
  currencyFor,
  findCity,
  formatMoney,
  GRAMS_PER_TOLA,
  karatLabel,
  KARATS,
  PAKISTAN_CITIES,
  type Karat,
} from "@/lib/gold-data";
import { useGoldRates } from "@/lib/nonblocking-rates";
import { getPakistanCityMeta, PAKISTAN_CITY_META } from "@/lib/pakistan-city-data";

/**
 * PakistanCityContent — 11 unique content blocks + city-specific FAQ suite,
 * rendered below the price table on /pakistan/[city]. Fully client-safe and
 * driven by per-city hand-curated metadata (no AI, no fetch).
 *
 * Blocks (in order):
 *   1. Live-badge + today's headline sentence
 *   2. City snapshot (province, region, sarafa bazaar, jeweller hubs)
 *   3. Karat-preference & specialty
 *   4. Rate comparison vs reference city + national reference (Karachi)
 *   5. Weight-unit conversion table (per gram / per tola / per 10g / per ratti)
 *   6. Making charges + tax note (locality context)
 *   7. Buying-time guidance
 *   8. Where to buy (local hubs list) — informational, no endorsements
 *   9. Nearby cities cluster (same province + reference city)
 *  10. Historical context / trend guidance
 *  11. City-specific FAQs (5 unique questions using city metadata)
 */
export function PakistanCityContent({ citySlug }: { citySlug: string }) {
  const meta = getPakistanCityMeta(citySlug);
  const city = findCity("pakistan", citySlug);
  const { data } = useGoldRates();
  if (!meta || !city) return null;

  const currency = currencyFor("pakistan");
  const prices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: data.pkrPerUsd,
    country: "pakistan",
    cityPremium: city.premium,
  });
  const karachi = PAKISTAN_CITIES.find((c) => c.slug === "karachi")!;
  const karachiPrices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: data.pkrPerUsd,
    country: "pakistan",
    cityPremium: karachi.premium,
  });
  const refCitySlug = slugify(meta.refCity);
  const refCityInfo = PAKISTAN_CITIES.find((c) => c.slug === refCitySlug);
  const refPrices = refCityInfo
    ? computeCityPrices({
        spotUsdPerOz: data.spotUsdPerOz,
        fxRate: data.pkrPerUsd,
        country: "pakistan",
        cityPremium: refCityInfo.premium,
      })
    : karachiPrices;

  const diffVsKarachi = prices.perTola24 - karachiPrices.perTola24;
  const diffPct = karachiPrices.perTola24
    ? ((diffVsKarachi / karachiPrices.perTola24) * 100).toFixed(2)
    : "0.00";
  const preferredLabel = karatLabel(meta.preferredKarat as Karat);
  const preferredPerTola =
    meta.preferredKarat === "22k"
      ? prices.perTola22
      : meta.preferredKarat === "21k"
        ? prices.perTola21
        : prices.perTola24;

  const nearby = PAKISTAN_CITIES.filter(
    (c) => c.slug !== citySlug && (c.slug === refCitySlug || sameProvince(c.slug, citySlug)),
  ).slice(0, 6);

  const faqs = buildCitySpecificFaqs({
    cityName: city.name,
    meta,
    preferredLabel,
    preferredPerTola,
    currency,
    diffPct,
    perTola24: prices.perTola24,
    perGram24: prices.perGram24,
  });

  return (
    <section aria-labelledby={`pk-city-content-${citySlug}`} className="mt-8 space-y-6">
      <h2 id={`pk-city-content-${citySlug}`} className="sr-only">
        {city.name} gold rate — detailed guide
      </h2>

      {/* Block 1 — live badge + headline */}
      <div className="flex flex-wrap items-center gap-3">
        <LiveBadge label={`${city.name} Live`} />
        <p className="text-sm text-muted-foreground">
          Aaj {city.name} mein 24K sona <strong>{formatMoney(prices.perTola24, currency)}</strong> per tola
          hai · 22K <strong>{formatMoney(prices.perTola22, currency)}</strong>.
        </p>
      </div>

      {/* Block 2 — city snapshot */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">{city.name} snapshot</h3>
        <dl className="mt-3 grid gap-2 sm:grid-cols-2 text-sm">
          <Row label="Province" value={meta.province} />
          <Row label="Region" value={meta.region} />
          <Row label="Main sarafa bazaar" value={meta.sarafa} />
          <Row label="Reference market" value={meta.refCity} />
        </dl>
        <p className="mt-3 text-sm text-muted-foreground">{meta.notes}</p>
      </div>

      {/* Block 3 — karat preference + specialty */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">
          {city.name} mein kaunsa karat sabse zyada chalta hai?
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {city.name} ke jeweller mostly <strong>{preferredLabel}</strong> gold quote karte hain
          jewellery ke liye — aaj ka rate{" "}
          <strong>{formatMoney(preferredPerTola, currency)}</strong> per tola. {meta.specialty}
        </p>
      </div>

      {/* Block 4 — reference rate comparison */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">
          {city.name} vs Karachi (national reference)
        </h3>
        <div className="mt-3 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="px-3 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-right">24K / tola</th>
                <th className="px-3 py-2 text-right">22K / tola</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <th scope="row" className="px-3 py-2 text-left font-medium">Karachi (reference)</th>
                <td className="px-3 py-2 text-right tabular-nums">{formatMoney(karachiPrices.perTola24, currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatMoney(karachiPrices.perTola22, currency)}</td>
              </tr>
              {refCityInfo && refCitySlug !== "karachi" && (
                <tr className="border-t border-border">
                  <th scope="row" className="px-3 py-2 text-left font-medium">{meta.refCity} (regional ref)</th>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(refPrices.perTola24, currency)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoney(refPrices.perTola22, currency)}</td>
                </tr>
              )}
              <tr className="border-t border-border bg-primary/5">
                <th scope="row" className="px-3 py-2 text-left font-semibold">{city.name}</th>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(prices.perTola24, currency)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatMoney(prices.perTola22, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {city.name} ka premium Karachi se{" "}
          <strong>
            {Number(diffPct) === 0
              ? "same"
              : `${diffPct}% ${Number(diffPct) > 0 ? "zyada" : "kam"}`}
          </strong>{" "}
          hai ({formatMoney(Math.abs(diffVsKarachi), currency)} per tola). Ye difference dealer margin +
          transport + local demand mila-jula banta hai.
        </p>
      </div>

      {/* Block 5 — weight unit conversion */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">Weight units mein {city.name} ka 24K rate</h3>
        <div className="mt-3 overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="px-3 py-2 text-left">Unit</th>
                <th className="px-3 py-2 text-right">Grams</th>
                <th className="px-3 py-2 text-right">24K rate</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: "Per Ratti", grams: 0.121 },
                { label: "Per Gram", grams: 1 },
                { label: "Per 10 Grams", grams: 10 },
                { label: `Per Tola (${GRAMS_PER_TOLA}g)`, grams: GRAMS_PER_TOLA },
              ].map((r) => (
                <tr key={r.label} className="border-t border-border">
                  <th scope="row" className="px-3 py-2 text-left font-medium">{r.label}</th>
                  <td className="px-3 py-2 text-right tabular-nums">{r.grams}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">
                    {formatMoney(prices.perGram24 * r.grams, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Block 6 — making charges + tax note */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">Making charges aur tax — {city.name}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {city.name} ke jewellers usually <strong>8%–20% making charges</strong> add karte hain
          depending on design complexity (plain bangles kam, kundan/meenakari zyada). Retail bullion
          par <strong>17% GST</strong> applicable hai jewellery form mein. Coin/bar form ke liye SRO
          exemptions available hain — bara khareed karne se pehle jeweller se written breakup
          zaroor le lein.
        </p>
      </div>

      {/* Block 7 — buying-time guidance */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">{city.name} mein khareed ka best time</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Rate hourly change hoti hai international spot (XAU/USD) aur live USD/PKR ki base par.
          Historical trends mein <strong>mid-week (Tue–Wed) morning slots</strong> mein rate settle
          hota hai. {city.name} ke sarafa bazaar mein daily opening rate 9:30–10:00 AM PKT ke qareeb
          announce hoti hai. Bara khareed ke liye 3–5 din ki average dekh kar decide karna sahi hai.
        </p>
      </div>

      {/* Block 8 — jeweller hubs */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">{city.name} ke famous gold markets</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {city.name} mein gold shops mostly in areas hain:
        </p>
        <ul className="mt-2 flex flex-wrap gap-2 text-xs">
          {meta.jewellerHubs.map((h) => (
            <li key={h} className="rounded-full border border-border bg-muted/40 px-3 py-1">
              {h}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-muted-foreground">
          Yeh sirf informational list hai — DrHint kisi bhi jeweller ko endorse nahi karta. Har
          purchase se pehle current rate, purity certificate aur receipt zaroor le lein.
        </p>
      </div>

      {/* Block 9 — nearby cities */}
      {nearby.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="text-lg font-bold">{city.name} ke qareeb ka rate check karein</h3>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {nearby.map((n) => (
              <Link
                key={n.slug}
                to="/$country/$city"
                params={{ country: "pakistan", city: n.slug }}
                className="rounded-lg border border-border px-3 py-2 text-sm hover:border-primary hover:text-primary"
              >
                {n.name} gold rate today →
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Block 10 — historical / trend context */}
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-lg font-bold">Trend guidance — {city.name}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {city.name} ka gold rate primarily 2 cheezon se drive hota hai: (1) international spot
          price XAU/USD, aur (2) USD/PKR exchange rate. Jab dollar Pakistan mein strong hota hai to
          local gold rate PKR mein automatically upar chala jata hai — chahe international spot flat
          ho. {meta.notes} Karat-wise short-form pages{" "}
          <Link to="/pakistan/24k" className="text-primary hover:underline">24K</Link>,{" "}
          <Link to="/pakistan/22k" className="text-primary hover:underline">22K</Link>,{" "}
          <Link to="/pakistan/21k" className="text-primary hover:underline">21K</Link> par bhi
          available hain.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {KARATS.filter((k) => k !== "14k").map((k) => (
            <Link
              key={k}
              to="/city/$country/$city/$karat"
              params={{ country: "pakistan", city: citySlug, karat: k }}
              className="rounded-full border border-border px-3 py-1 hover:border-primary hover:text-primary"
            >
              {karatLabel(k)} in {city.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Block 11 — City-specific FAQs */}
      <FaqSection faqs={faqs} title={`${city.name} gold rate — city-specific FAQs`} />
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-1">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}

function slugify(name: string): string {
  const map: Record<string, string> = {
    Karachi: "karachi",
    Lahore: "lahore",
    Islamabad: "islamabad",
    Rawalpindi: "rawalpindi",
    Peshawar: "peshawar",
    Quetta: "quetta",
    Multan: "multan",
    Faisalabad: "faisalabad",
    Hyderabad: "hyderabad-pk",
    Sialkot: "sialkot",
    Gujranwala: "gujranwala",
    Bahawalpur: "bahawalpur",
    Sargodha: "sargodha",
    Sahiwal: "sahiwal",
    Sheikhupura: "sheikhupura",
    Sukkur: "sukkur",
    Larkana: "larkana",
    Mardan: "mardan",
    Abbottabad: "abbottabad",
  };
  return map[name] ?? name.toLowerCase();
}

function sameProvince(a: string, b: string): boolean {
  const A = PAKISTAN_CITY_META_LOOKUP[a];
  const B = PAKISTAN_CITY_META_LOOKUP[b];
  return !!A && !!B && A === B;
}

// Precomputed province index for O(1) sibling check.
const PAKISTAN_CITY_META_LOOKUP: Record<string, string> = Object.fromEntries(
  Object.entries(PAKISTAN_CITY_META).map(([slug, m]) => [slug, m.province]),
);

function buildCitySpecificFaqs(args: {
  cityName: string;
  meta: ReturnType<typeof getPakistanCityMeta> & object;
  preferredLabel: string;
  preferredPerTola: number;
  currency: "PKR" | "INR";
  diffPct: string;
  perTola24: number;
  perGram24: number;
}) {
  const { cityName, meta, preferredLabel, preferredPerTola, currency, diffPct, perTola24, perGram24 } = args;
  return [
    {
      q: `Aaj ${cityName} mein 24K sone ka rate kya hai?`,
      a: `${cityName} mein aaj 24K gold ka rate ${formatMoney(perTola24, currency)} per tola aur ${formatMoney(perGram24, currency)} per gram hai. Ye rate ${meta.sarafa} ke reference se update hota hai every 10 minutes, international spot + live USD/PKR ke hisaab se.`,
    },
    {
      q: `${cityName} mein ${preferredLabel} gold zyada kyun bikta hai?`,
      a: `${cityName} mein jewellery ke liye ${preferredLabel} sabse popular hai kyunki ${meta.specialty} Aaj ${preferredLabel} ka rate ${formatMoney(preferredPerTola, currency)} per tola hai.`,
    },
    {
      q: `${cityName} aur Karachi ke rate mein kitna difference hai?`,
      a: `${cityName} ka gold rate Karachi (national reference) se ${Number(diffPct) === 0 ? "almost same" : `${diffPct}% ${Number(diffPct) > 0 ? "zyada" : "kam"}`} hai. Ye difference ${meta.province} ke transport cost aur ${meta.sarafa} ke local dealer margin ki wajah se banta hai. ${meta.refCity} ke dealers ${cityName} ke liye daily opening rate reference dete hain.`,
    },
    {
      q: `${cityName} mein gold khareedne ke liye kaunsa market best hai?`,
      a: `${cityName} ke main gold markets ye hain: ${meta.jewellerHubs.join(", ")}. Sabse purana aur bara sarafa ${meta.sarafa} hai. Purchase se pehle current rate written mein le lein, purity certificate check karein, aur receipt zaroor rakhein — DrHint kisi bhi individual jeweller ko endorse nahi karta.`,
    },
    {
      q: `${cityName} mein sone ka rate roz kyun change hota hai?`,
      a: `${cityName} ka rate directly international XAU/USD spot + USD/PKR exchange rate se calculate hota hai. Har minute international market move hoti hai (Fed policy, dollar strength, geopolitics), aur PKR bhi shift hota hai. Iske upar ${cityName} ka local ${(meta.populationBand === "small" ? "0.13–0.16%" : meta.populationBand === "mid" ? "0.10–0.14%" : "0.05–0.12%")} premium add hota hai transport aur dealer margin ke liye. Total rate hourly move karta rehta hai.`,
    },
  ];
}